import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import LayoutAction from "../../actions/LayoutAction";
import { Button, InputField } from "../../components/ui";
import type { GtmLinkedInSequenceState, GtmSessionStatus } from "../api/GtmApi";
import { GTM_SEQUENCE_PAUSED_KEEP_STEP } from "../disclosureCopy";
import { useGtmBrainPatchMutation, useGtmBrainQuery } from "../hooks/useGtmBrain";
import {
  useGtmSessionDisconnectMutation,
  useGtmSessionQuery,
  useGtmSessionSaveMutation,
  useGtmSessionSequenceMutation,
} from "../hooks/useGtmSession";

function statusLabel(status: GtmSessionStatus["status"]) {
  switch (status) {
    case "connected":
      return "Connected";
    case "rejected":
      return "Rejected — reconnect";
    default:
      return "Not connected";
  }
}

function statusClass(status: GtmSessionStatus["status"]) {
  switch (status) {
    case "connected":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function GtmSettingsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const isAdmin = useSelector((state: any) => !!state.userReducer.getIn(["currentUser", "admin"]));

  const [liAt, setLiAt] = useState("");
  const [calendarUrl, setCalendarUrl] = useState("");

  const sessionQuery = useGtmSessionQuery(projectId);
  const brainQuery = useGtmBrainQuery(projectId);
  const saveSessionMutation = useGtmSessionSaveMutation(projectId);
  const disconnectMutation = useGtmSessionDisconnectMutation(projectId);
  const sequenceMutation = useGtmSessionSequenceMutation(projectId);
  const brainPatchMutation = useGtmBrainPatchMutation(projectId);

  const session: GtmSessionStatus = sessionQuery.data || {
    status: "missing",
    sequence: "paused",
  };
  const loading = sessionQuery.isLoading || brainQuery.isLoading;
  const saving =
    saveSessionMutation.isPending ||
    disconnectMutation.isPending ||
    sequenceMutation.isPending;
  const savingCalendar = brainPatchMutation.isPending;

  useEffect(() => {
    document.title = "GTM · Settings";
  }, []);

  useEffect(() => {
    if (brainQuery.data?.brain?.calendarUrl != null) {
      setCalendarUrl(brainQuery.data.brain.calendarUrl || "");
    }
  }, [brainQuery.data?.brain?.calendarUrl]);

  useEffect(() => {
    if (!sessionQuery.isError && !brainQuery.isError) return;
    const err: any = sessionQuery.error || brainQuery.error;
    dispatch(
      LayoutAction.setNotification({
        title: t("Settings"),
        type: "error",
        message: err?.response?.data?.message || err?.message || t("Failed to load settings"),
      }) as any
    );
  }, [brainQuery.error, brainQuery.isError, dispatch, sessionQuery.error, sessionQuery.isError, t]);

  const onSaveSession = async () => {
    if (!isAdmin) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Settings"),
          type: "error",
          message: t("Workspace Admin required to connect LinkedIn"),
        }) as any
      );
      return;
    }
    if (!liAt.trim()) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Settings"),
          type: "error",
          message: t("Paste the LinkedIn li_at session cookie"),
        }) as any
      );
      return;
    }
    try {
      const status = await saveSessionMutation.mutateAsync({
        liAt: liAt.trim(),
        tosAcknowledged: true,
      });
      setLiAt("");
      dispatch(
        LayoutAction.setNotification({
          title: t("LinkedIn session"),
          type: status.status === "connected" ? "success" : "error",
          message:
            status.status === "connected"
              ? t("Session connected")
              : t("LinkedIn rejected this session — paste a fresh cookie"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("LinkedIn session"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Failed to save session"),
        }) as any
      );
    }
  };

  const onDisconnect = async () => {
    if (!isAdmin) return;
    try {
      await disconnectMutation.mutateAsync();
      setLiAt("");
      dispatch(
        LayoutAction.setNotification({
          title: t("LinkedIn session"),
          type: "success",
          message: t("Session disconnected"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("LinkedIn session"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Disconnect failed"),
        }) as any
      );
    }
  };

  const onToggleSequence = async () => {
    if (!isAdmin || session.status !== "connected") return;
    const next: GtmLinkedInSequenceState =
      session.sequence === "running" ? "paused" : "running";
    try {
      await sequenceMutation.mutateAsync(next);
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Sequence"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Failed to update sequence"),
        }) as any
      );
    }
  };

  const onSaveCalendar = async () => {
    try {
      await brainPatchMutation.mutateAsync({ calendarUrl: calendarUrl.trim() });
      dispatch(
        LayoutAction.setNotification({
          title: t("Calendar URL"),
          type: "success",
          message: t("Calendar URL saved to brain"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Calendar URL"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Save failed"),
        }) as any
      );
    }
  };

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="px-4 py-3 border-b border-gray-100">
        <h1 className="text-lg font-semibold text-gray-900">{t("Settings")}</h1>
        <p className="text-sm text-gray-500">
          {t("LinkedIn session, sequence pause, calendar URL, and Automation link.")}
        </p>
      </div>

      <div className="mx-auto w-full max-w-2xl space-y-8 px-4 py-6">
        {loading ? (
          <p className="text-sm text-gray-500">{t("Loading…")}</p>
        ) : (
          <>
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">{t("LinkedIn session")}</h2>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(
                    session.status
                  )}`}
                >
                  {t(statusLabel(session.status))}
                </span>
              </div>

              {!isAdmin ? (
                <p className="text-sm text-gray-500">
                  {t("Only a workspace Admin can connect or disconnect the LinkedIn session.")}
                </p>
              ) : (
                <>
                  <InputField
                    label={t("Member session cookie (li_at)")}
                    type="password"
                    value={liAt}
                    onChange={setLiAt}
                    placeholder={t("Paste li_at value — never shared back by the API")}
                    disabled={saving}
                    autoFocus={false}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      isLoading={saving}
                      disabled={!liAt.trim()}
                      onClick={onSaveSession}
                    >
                      {t("Save session")}
                    </Button>
                    {session.status !== "missing" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={saving}
                        onClick={onDisconnect}
                      >
                        {t("Disconnect")}
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </section>

            <section className="space-y-3 border-t border-gray-100 pt-6">
              <h2 className="text-sm font-semibold text-gray-900">{t("Sequence")}</h2>
              <p className="text-sm text-gray-500">{t(GTM_SEQUENCE_PAUSED_KEEP_STEP)}</p>
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                    session.sequence === "running"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {session.sequence}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!isAdmin || session.status !== "connected" || saving}
                  onClick={onToggleSequence}
                >
                  {session.sequence === "running" ? t("Pause") : t("Resume")}
                </Button>
              </div>
            </section>

            <section className="space-y-3 border-t border-gray-100 pt-6">
              <h2 className="text-sm font-semibold text-gray-900">{t("Calendar URL")}</h2>
              <p className="text-sm text-gray-500">
                {t("Stored on the GTM brain and copied when a lead reaches meeting.")}
              </p>
              <InputField
                label={t("Meeting / calendar link")}
                value={calendarUrl}
                onChange={setCalendarUrl}
                placeholder="https://"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={savingCalendar}
                onClick={onSaveCalendar}
              >
                {t("Save calendar URL")}
              </Button>
            </section>

            <section className="space-y-2 border-t border-gray-100 pt-6">
              <h2 className="text-sm font-semibold text-gray-900">{t("Automation")}</h2>
              <p className="text-sm text-gray-500">
                {t(
                  "Hourly scheduled rule runs Advance GTM sequence. Ensure it exists from a Play Sequence tab, or open Automations to manage rules."
                )}
              </p>
              <Link
                className="text-sm font-medium text-blue-600 hover:underline"
                to={`/workspaces/${projectId}/automations?key=Automations`}
              >
                {t("Open Automations")}
              </Link>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
